import React from "react";
import { EWsrvTransform } from "../../lib/Media/util";
import { Carousel } from "../Carousel/Carousel";
import { ListingGallery } from "../Gallery/ListingGallery";
import ListingCard, { EListingCardLayouts } from "./ListingCard";
import { ListingContact } from "./ListingContact";
import ListingDetails from "./ListingDetails";
import ListingMap from "./ListingMap";
import { ListingSummary } from "./ListingSummary";
import { EListingVitalsLayout, ListingVitals } from "./ListingVitals";
import { EDisclaimerLocation, ELisitngFormLocation, EMediaSectionLayout, IListingLayoutProps } from "./util";
import { ContactForm } from "../Forms";
import { BackgroundImage } from "../Media";
import TabbedContentBlade, { ITabContent } from "../Blades/TabbedContentBlade";
import { IListing } from "../../lib";
import { WsrvImage } from "../Media";
import ListingDocuments from "./ListingDocuments";
import { ListingOpenHouse } from "./ListingOpenHouse";
import { ELocationSearchLayout } from "../Forms/LocationSearch";
import { ListingFlag } from "./ListingFlag";
import { SliderGallery } from "../Gallery";

export const ListingLayoutSingleColumn: React.FC<IListingLayoutProps> = ({
  listing,
  title,
  linkFunc,
  similar,
  agents,
  className = "",
  similarCardProps,
  carouselProps,
  similarCarouselProps,
  galleryProps,
  detailsProps,
  vitalsProps,
  contactProps,
  formProps,
  summaryProps,
  mapProps,
  rowClassName = "",
  showContactUsForm,
  contactUsFormProps,
  documentsProps,
  photosTabTitle,
  propertyVideoTabTitle,
  virtualTourTabTitle,
  floorplansTabTitle,
  displayDisclaimer = false,
  disclaimerCssClasses = "",
  disclaimers,
  subtitle,
  formColumnClassName = "",
  heroWrapperClassName = "",
  mediaSectionLayout = EMediaSectionLayout.tabs,
  mediaSectionTitle,
  listingFormLocation = ELisitngFormLocation.summarySide,
  showFlags = false,
  disclaimerLocation = EDisclaimerLocation.bottom,
  optionalFormChildrenProp,
  showForm = true
}) => {
  const MediaSection: React.FC<IListing> = (listing) => {
    if (mediaSectionLayout == EMediaSectionLayout.tabs) {
      if (listing.videos || listing.tours || listing.floorplans) {
        let tabContent: ITabContent[] = [
          {
            tabTitle: photosTabTitle ?? "Photos",
            contentClassName: "max-w-full",
            content: (
              <SliderGallery
                showFullscreen={true}
                preloadCount={25}
                eagerLoadCount={25}
                images={listing.images?.map((image, index) => ({
                  id: index,
                  src: image.url,
                  thumbnail: image.thumbnailURL || image.url,
                  alt: image.caption || listing.address.text || `Image ${index + 1}`,
                  caption: image.caption
                })) || []}
                {...galleryProps}
              />
            ),
          },
        ];
        if (listing.videos && listing.videos.length > 0) {
          tabContent.push({
            tabTitle: propertyVideoTabTitle ?? "Property Video",
            contentClassName: "max-w-full w-full",
            content: (
              <iframe
                src={listing.videos[0].url}
                allowFullScreen
                allow="encrypted-media"
                className="w-full aspect-video"
              />
            ),
          });
        }
        if (listing.tours && listing.tours.length > 0) {
          tabContent.push({
            tabTitle: virtualTourTabTitle ?? "Virtual Tour",
            contentClassName: "max-w-full w-full",
            content: (
              <iframe
                src={listing.tours[0].url}
                className="w-full aspect-video"
              />
            ),
          });
        }
        if (listing.floorplans && listing.floorplans.length > 0) {
          tabContent.push({
            tabTitle: floorplansTabTitle ?? "Floor Plans",
            content: (
              <>
                {listing.floorplans.map((plan, index) => (
                  <img src={plan.url} key={index} />
                ))}
              </>
            ),
          });
        }
        return (
          <TabbedContentBlade
            title={mediaSectionTitle ?? "Media"}
            className="max-w-screen-xl mx-auto"
            tabListClassName="inverted"
            tabs={tabContent}
          />
        );
      } else {
        return (
          <ListingGallery
            listing={listing}
            preset={EWsrvTransform.cover16x9}
            width={1600}
            height={900}
            {...galleryProps}
          />
        );
      }
    }
    else {
      return (
        <section className="listing-media-section max-w-screen-xl mx-auto flex flex-col items-center">
          <h2>{mediaSectionTitle ?? "Media"}</h2>
          {listing.images && listing.images.length > 0 && (
            <>
              <h3>{photosTabTitle ?? "Photos"}</h3>
              <ListingGallery
                listing={listing}
                preset={EWsrvTransform.cover16x9}
                width={1600}
                height={900}
                {...galleryProps}
              />
            </>
          )}
          {listing.videos && listing.videos.length > 0 && (
            <>
              <h3>{propertyVideoTabTitle ?? "Property Video"}</h3>
              <iframe
                src={listing.videos[0].url}
                allowFullScreen
                allow="encrypted-media"
                className="w-full aspect-video"
              />
            </>
          )}
          {listing.tours && listing.tours.length > 0 && (
            <>
              <h3>{propertyVideoTabTitle ?? "Virtual Tour"}</h3>
              <iframe
                src={listing.tours[0].url}
                className="w-full aspect-video"
              />
            </>
          )}
        </section>
      )
    }
  };

  const Disclaimer: React.FC = () => {
    return (
      <>
        {displayDisclaimer && (
          <section className={disclaimerCssClasses} dangerouslySetInnerHTML={{ __html: disclaimers?.find(x => x.mlsSourceName === listing.mlsSourceName)?.disclaimerText ?? "" }}>
          </section>
        )}
      </>
    )
  }

  if (optionalFormChildrenProp) {
    formProps!.children = optionalFormChildrenProp(listing)
  }

  return (
    <div className={`${className} listing-layout layout-single-column`}>
      {listing.images &&
        (carouselProps == null ?
          <BackgroundImage src={listing.images[0].url}>
            <div
              className={`relative w-full h-screen flex flex-col items-center justify-end mb-10 bg-gradient-to-t from-black/80 via-transparent to-transparent ${heroWrapperClassName}`}
            >
              <ListingVitals listing={listing} {...vitalsProps} />
            </div>
          </BackgroundImage>
          :
          <div className={`relative w-full h-screen mb-10 ${heroWrapperClassName}`}>
            <Carousel
              className="w-full h-screen"
              {...carouselProps}
            >
              {listing.images?.map((img, index) => (
                <WsrvImage
                  lazy={false}
                  urlParams={{
                    originURL: img.url,
                    preset: EWsrvTransform.cover16x9,
                    width: 1920,
                  }}
                  widthMax={1920}
                  imgProps={{
                    className: "w-full h-screen mx-auto object-cover",
                    width: img.dimensions?.[0],
                    height: img.dimensions?.[1],
                    placeholder: <span style={undefined} />,
                    wrapperProps: {
                      style: {},
                    },
                    alt: img.caption || listing.address.text,
                  }}
                />
              ))}
            </Carousel>
            {showFlags && listing.flags?.[0] && <ListingFlag className="absolute top-0 left-0 bg-accent text-action-text" flags={listing.flags} />}
            <div className="absolute top-1/2 right-0 bottom-0 left-0 flex flex-col items-center justify-end bg-gradient-to-t from-black/80 via-black/40 to-transparent">
              <ListingVitals listing={listing} {...vitalsProps} />
            </div>
          </div>
        )
      }
      <div className={`md:grid md:grid-cols-3 md:space-x-5 ${rowClassName}`}>
        <div className="md:col-span-2 mb-3">
          {title && <h1>{title}</h1>}
          <ListingSummary listing={listing} title={subtitle} {...summaryProps} />
        </div>
        <div className={`md:col-span-1 mb-3 ${formColumnClassName}`}>
          <ListingOpenHouse openHouse={listing.openHouse} className="mb-3" />
          {showForm && listingFormLocation == ELisitngFormLocation.summarySide && (
            <ContactForm {...formProps}>
              <ListingContact
                listing={listing}
                agents={agents}
                offices={listing.office}
                linkFunc={linkFunc}
                {...contactProps}
              />
            </ContactForm>
          )}
        </div>
      </div>
      {listing.propertyDocuments && listing.propertyDocuments.length > 0 && (
        <ListingDocuments documents={listing.propertyDocuments} {...documentsProps} />
      )}
      <MediaSection {...listing} />
      {listing.details && listing.details.length > 0 && (
        <ListingDetails
          detailGroups={listing.details}
          className="py-3"
          {...detailsProps}
        />
      )}
      {displayDisclaimer && disclaimerLocation == EDisclaimerLocation.afterDetails && (
        <Disclaimer />
      )}
      {listing.location && listing.location?.lat && listing.location.lng && (
        <ListingMap listing={listing} title="Location" {...mapProps} />
      )}
      {showForm && listingFormLocation == ELisitngFormLocation.pageSection && (
        <ContactForm {...formProps}>
        </ContactForm>
      )}
      {similar && similar.length > 2 && (
        <Carousel
          title="Similar"
          slickSettings={{
            infinite: true,
            centerMode: true,
            centerPadding: "10px",
            slidesToShow: Math.min(3, similar.length),
            arrows: true,
          }}
          className={`slick-center-zoom ${similarCarouselProps?.className || ""
            }`}
          {...similarCarouselProps}
        >
          {similar.map((listing, index) => {
            return (
              <ListingCard
                layout={EListingCardLayouts.vertical}
                vitalsLayout={EListingVitalsLayout.column2Border1}
                listing={listing}
                className="max-w-[680px] mx-auto"
                maxWidthImage={680}
                key={index}
                linkFunc={linkFunc}
                {...similarCardProps}
              />
            );
          })}
        </Carousel>
      )}
      {showContactUsForm && <ContactForm {...contactUsFormProps} />}
      {displayDisclaimer && disclaimerLocation == EDisclaimerLocation.bottom && (
        <Disclaimer />
      )}
    </div>
  );
};
